import { useMemo } from 'react';
import { useSessionStore } from '../session/store/useSessionStore';
import { useBusinessProfileStore } from '../session/store/useBusinessProfileStore';
import { useGlobalFeatureFlags } from '../providers/GlobalFeatureFlagsProvider';
import { usePlanEntitlements } from '../providers/PlanEntitlementsProvider';
import { isAiChatFeatureOn, isGlobalFeatureOn } from '../feature-flags/globalFeatureFlagsService';
import { planAllowsCustomer, planAllowsPromotions } from '../feature-flags/planEntitlementsService';
import { useEffectiveBusinessCapabilities } from '../session/hooks/useEffectiveBusinessCapabilities';
import { isPublicStoreFeaturesVisible } from '../feature-flags/publicStoreFeaturesGate';
import { isOperationModeStorefrontOff, parseOperationMode } from '../session/domain/operationMode';
import { isBillingEnabled } from '../billing/isBillingEnabled';
import {
  buildNavVisibilityContext,
  type NavVisibilityContext,
} from './navigationManifest';
import type { PlanEntitlementsRow } from '../feature-flags/domain/planEntitlements';
import type { GlobalFeatureFlagsMap } from '../feature-flags/domain/globalFeatureFlags';
import { useDay1PathNavStore } from '../../onboarding/store/useDay1PathNavStore';

export function useNavVisibilityContext(
  entitlements?: PlanEntitlementsRow | null,
  globalFeatureFlags?: GlobalFeatureFlagsMap | null,
): NavVisibilityContext {
  const currentBranch = useSessionStore((s) =>
    s.branches.find((b) => b.id === s.currentBranchId),
  );
  const businessTypeCode = useBusinessProfileStore((s) => s.profile?.business_type_code ?? null);
  const businessConfig = useBusinessProfileStore((s) => s.profile?.business_config);
  const storefrontEnabled = !isOperationModeStorefrontOff(parseOperationMode(businessConfig));
  const entFromHook = usePlanEntitlements();
  const flagsFromHook = useGlobalFeatureFlags();
  const effectiveCapabilities = useEffectiveBusinessCapabilities();
  const day1PreferCatalogTab = useDay1PathNavStore((s) => s.preferCatalogTab);
  const day1CollapseStorefrontNav = useDay1PathNavStore((s) => s.collapseStorefrontNav);
  const day1NavLoaded = useDay1PathNavStore((s) => s.loaded);
  const storePublished = useDay1PathNavStore((s) => s.storePublished);

  const ent = entitlements ?? entFromHook.entitlements;
  const flags = globalFeatureFlags ?? flagsFromHook.flags;

  const customerTabAllowed =
    isGlobalFeatureOn(flags, 'fiado_integrations') && planAllowsCustomer(ent);
  const promotionsNavAllowed =
    isGlobalFeatureOn(flags, 'promotions_integrations') && planAllowsPromotions(ent);
  const priceListsNavAllowed = isGlobalFeatureOn(flags, 'price_lists_integrations');
  const loyaltyNavAllowed = isGlobalFeatureOn(flags, 'loyalty_integrations');
  const expensesNavAllowed = isGlobalFeatureOn(flags, 'expenses_module');
  const aiNavAllowed = isAiChatFeatureOn(flags);

  const billingNavAllowed = useMemo(() => {
    // Mirror isBillingEnabled (opt-in), but bind to provider flags for re-render.
    const env = process.env.EXPO_PUBLIC_BILLING_ENABLED;
    if (env === 'false') return false;
    if (env === 'true') return true;
    if (!flags) return isBillingEnabled();
    return flags.billing_enabled === true;
  }, [flags]);
  const publicStoreFeaturesAllowed = isPublicStoreFeaturesVisible(
    flags,
    businessTypeCode,
    storefrontEnabled,
  );
  // Consultations: farmacia o rubros con agenda / catálogo de servicios (no códigos ERP hardcodeados).
  const pharmacyConsultationsAllowed =
    businessTypeCode === 'pharmacy_basic' ||
    effectiveCapabilities.has('appointments') ||
    effectiveCapabilities.has('service_catalog');

  return useMemo(
    () =>
      buildNavVisibilityContext({
        permissions: currentBranch?.permissions ?? [],
        customerTabAllowed,
        promotionsNavAllowed,
        priceListsNavAllowed,
        loyaltyNavAllowed,
        expensesNavAllowed,
        billingNavAllowed,
        publicStoreFeaturesAllowed,
        pharmacyConsultationsAllowed,
        aiNavAllowed,
        // Plan gate only; Team.view (or owner bootstrap) controls module visibility.
        teamNavAllowed: ent?.allow_employees !== false,
        effectiveCapabilities,
        day1PreferCatalogTab,
        day1CollapseStorefrontNav,
        day1NavLoaded,
        storePublished,
      }),
    [
      currentBranch?.permissions,
      customerTabAllowed,
      promotionsNavAllowed,
      priceListsNavAllowed,
      loyaltyNavAllowed,
      expensesNavAllowed,
      billingNavAllowed,
      publicStoreFeaturesAllowed,
      pharmacyConsultationsAllowed,
      aiNavAllowed,
      ent?.allow_employees,
      effectiveCapabilities,
      storefrontEnabled,
      day1PreferCatalogTab,
      day1CollapseStorefrontNav,
      day1NavLoaded,
      storePublished,
    ],
  );
}
